import {useMemo, useState} from "react";
import {Chip, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import {InvestigatorStat} from "../../../data/internal-affairs/ComplaintStatisticsResponse";

const cols: { key: keyof InvestigatorStat; label: string }[] = [
    {key: "nick",               label: "Investigator"},
    {key: "rankDisplayName",    label: "Rank"},
    {key: "activeComplaints",   label: "Active"},
    {key: "totalInvestigated",  label: "Closed"},
    {key: "sustained",          label: "Sustained"},
    {key: "exonerated",         label: "Exonerated"},
    {key: "notSustained",       label: "Not Sustained"},
    {key: "unfounded",          label: "Unfounded"},
    {key: "withdrawn",          label: "Withdrawn"},
    {key: "overdueOnCompletion",label: "Overdue"},
    {key: "avgDaysToClose",     label: "Avg Days"},
]

export function InvestigatorStatisticsTab({stats}: { stats: InvestigatorStat[] }) {
    const [sort, setSort] = useState<keyof InvestigatorStat>("totalInvestigated")

    const sorted = useMemo(() =>
        [...stats].sort((a, b) => {
            const av = a[sort] ?? 0
            const bv = b[sort] ?? 0
            return typeof av === "number" && typeof bv === "number" ? bv - av : String(av).localeCompare(String(bv))
        }), [stats, sort])

    return (
        <Paper variant="outlined" sx={{overflowX: "auto"}}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {cols.map(c => (
                            <TableCell key={c.key} onClick={() => setSort(c.key)}
                                       sx={{cursor: "pointer", fontWeight: sort === c.key ? 700 : 400,
                                            whiteSpace: "nowrap", userSelect: "none"}}>
                                {c.label}{sort === c.key ? " ↓" : ""}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sorted.map(s => (
                        <TableRow key={s.communityId}>
                            <TableCell>
                                <Link underline="none" component={RouterLink} to={`/user/${s.communityId}`}
                                      variant="body2" fontWeight={500}>{s.nick}</Link>
                            </TableCell>
                            <TableCell><Typography variant="body2" color="text.secondary">{s.rankDisplayName ?? "—"}</Typography></TableCell>
                            <TableCell><Chip label={s.activeComplaints} size="small" color={s.activeComplaints > 0 ? "info" : "default"} variant={s.activeComplaints > 0 ? "filled" : "outlined"}/></TableCell>
                            <TableCell><Chip label={s.totalInvestigated} size="small" color={s.totalInvestigated > 0 ? "primary" : "default"} variant={s.totalInvestigated > 0 ? "filled" : "outlined"}/></TableCell>
                            <TableCell><Typography variant="body2" color="error.main">{s.sustained}</Typography></TableCell>
                            <TableCell><Typography variant="body2" color="success.main">{s.exonerated}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{s.notSustained}</Typography></TableCell>
                            <TableCell><Typography variant="body2">{s.unfounded}</Typography></TableCell>
                            <TableCell><Typography variant="body2" color="text.secondary">{s.withdrawn}</Typography></TableCell>
                            <TableCell>
                                {s.overdueOnCompletion > 0
                                    ? <Chip label={s.overdueOnCompletion} size="small" color="warning" variant="outlined"/>
                                    : <Typography variant="body2" color="text.disabled">0</Typography>}
                            </TableCell>
                            <TableCell><Typography variant="body2">{s.avgDaysToClose != null ? `${s.avgDaysToClose}d` : "—"}</Typography></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    )
}
